import {
  Button,
  IconRemove,
  InputValidation,
  List,
  ListHeader,
  LoaderSpinner,
  SpaceBetween,
  spacing,
  styled,
  Validation,
} from '@lessonup/ui-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FullPageBodySection } from '../../../../../components/FullPageFlow/FullPageBodySection';
import { MaiaExample } from '../../../../../types/graphql/types.generated';
import { UploadFileForOCR } from '../../../dataComponents/UploadFileForOCR';
import { ContextUpload } from '../../../model';
import { ExamplesSection } from './ExamplesSection';

export const UploadImage = ({
  contextUploads,
  setContextUploads,
  promptInputLength,
  promptCharacterLimit,
  examples,
  onExamplesClick,
  selectedUploadExample,
}: {
  contextUploads: ContextUpload[];
  setContextUploads: React.Dispatch<React.SetStateAction<ContextUpload[]>>;
  promptInputLength: number;
  promptCharacterLimit: number;
  examples?: MaiaExample[];
  onExamplesClick: (examplePrompt: string) => void;
  selectedUploadExample?: string;
}) => {
  const { t } = useTranslation('chatGPT');

  const removeUploadFromList = (id: string) => {
    setContextUploads((uploads) => uploads.filter((upload) => upload.id !== id));
  };

  const getPromptValidation = (): InputValidation | undefined => {
    if (promptInputLength > promptCharacterLimit) {
      return {
        type: 'negative',
        message: t('promptCharactersWarning', { promptInputLength, promptCharacterLimit }),
      };
    }

    return undefined;
  };

  const validation = getPromptValidation();

  return (
    <SpaceBetween direction="y" spacing={spacing.size16}>
      <UploadFileForOCR setContextUploads={setContextUploads} />
      <FullPageBodySection>
        {contextUploads.length === 0 && examples && (
          <FullWidth>
            <ExamplesSection
              examples={examples}
              isContextMode={true}
              onExamplesClick={onExamplesClick}
              selectedUploadExample={selectedUploadExample}
            />
          </FullWidth>
        )}
        {contextUploads.length > 0 && (
          <FullWidth>
            <StyledUploadWrapper direction="y" spacing={spacing.size16} padding={spacing.size4}>
              <ListHeader text={t('uploadStep.imagesTitle')} priority="high" indentation={false} />
              <StyledList
                items={contextUploads.map((upload) => {
                  return {
                    icon: upload.isLoading ? <LoaderSpinner /> : undefined,
                    text: upload.fileName,
                    indentation: false,
                    endSlot: (
                      <Button
                        buttonType="negative"
                        size="small"
                        aria-label={t('uploadStep.btnDelete')}
                        onClick={() => removeUploadFromList(upload.id)}
                        iconStart={<IconRemove />}
                      />
                    ),
                  };
                })}
              />
              {validation && (
                <Validation
                  id="upload-step-validation"
                  size="medium"
                  type={validation.type}
                  message={validation.message}
                />
              )}
            </StyledUploadWrapper>
          </FullWidth>
        )}
      </FullPageBodySection>
    </SpaceBetween>
  );
};

const StyledUploadWrapper = styled(SpaceBetween)`
  width: 100%;
  height: 100%;
  ul {
    flex-grow: 1;
  }
`;

const StyledList = styled(List)`
  width: 100%;
  word-break: break-all;
`;

const FullWidth = styled.div`
  width: 100%;
`;
